/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { request } from '@/services/request';
import { formatDateForMeasure } from '@/utils/formatDateForMeasure';
import { getAlertFactConfig } from '@/utils/getAlertFactConfig';
import { getTenantTimeRange } from '@/utils/getTenantTimeRange';
import { AppGraph } from 'types/api-data';
import { MeasuresCount, MeasuresData } from 'types/measures-api-data';

import { useDaysConversion } from '@/hooks/useDaysConversion';
import { FACT_VIEW_SUPPORT_MAP } from '@/components/display-page/traces/useTracesFilters';

import {
  getPresetLabelToNameMap,
  getPresetRange,
  usePresets,
} from '../display-page/prompts/date-range-picker/calendar-helper/presets';
import { System } from '../icons';
import { Separator } from '../ui/separator';
import { Skeleton } from '../ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { ToFromDateRange } from './title-bar';

export const SeparatorComp = () => (
  <Separator
    className="h-full min-h-7 bg-line-tertiary"
    orientation="vertical"
  />
);

type TracesMeasurePillProps = {
  measures: MeasuresCount;
  data: AppGraph;
  selectedPreset: string | null | undefined;
  toFromDateRange: ToFromDateRange;
  lastDiscoveredAt?: string | null;
};
const TracesMeasurePill = ({
  measures,
  data,
  selectedPreset,
  toFromDateRange,
}: TracesMeasurePillProps) => {
  const params = useParams();
  const searchParams = useSearchParams();
  const { messages } = useLanguage();
  const pathname = usePathname();

  const presets = usePresets();
  const tabName = pathname.split('/')[4];

  const [initialRange, setInitialRange] = useState<{
    start: string | null;
    end: string | null;
  }>({
    start: null,
    end: null,
  });

  useEffect(() => {
    const presetMap = getPresetLabelToNameMap(presets);

    const tenantDefaultTimeRange = getTenantTimeRange();
    const defaultTimeRange = tenantDefaultTimeRange || data.default_time_range;
    const range = getPresetRange(presetMap[String(defaultTimeRange)], presets);

    if (range?.from && range?.to) {
      setInitialRange({
        start: range.from.toISOString(),
        end: range.to.toISOString(),
      });
    }
  }, [tabName]);

  const startParam = searchParams.get('start');
  const endParam = searchParams.get('end');

  const dateRangeParam = searchParams.get('days');

  const [measuresData, setMeasuresData] = useState<MeasuresCount>(measures);
  const [loading, setLoading] = useState(false);
  const prevStartParam = useRef<string | null>(null);
  const prevEndParam = useRef<string | null>(null);
  const prevDateRangeParam = useRef<string | null>(null);
  // Tracks the time range actually used in the last fetch
  const [activeFetchRange, setActiveFetchRange] = useState<{
    start: string | null;
    end: string | null;
  }>({ start: null, end: null });

  const daysCountString = useDaysConversion(
    selectedPreset,
    toFromDateRange,
    activeFetchRange.start ?? undefined,
    activeFetchRange.end ?? undefined,
    dateRangeParam
  );

  // Check if user-selected time range differs from default
  const isUserSelectedTimeRange =
    startParam !== initialRange.start || endParam !== initialRange.end;

  const slug = Array.isArray(params.slug) ? params.slug[0] : params.slug;

  const fetchMeasures = useCallback(
    async (start: string | null, end: string | null) => {
      let queryParams: Record<string, string> | undefined;

      if (start && end) {
        queryParams = {
          start_time: start,
          end_time: end,
        };
      }

      try {
        setLoading(true);
        const response: any = await request.post(
          {
            endpoint: 'insights/measures/query',
            queryParams,
          },
          { app_names: [slug] }
        );

        const newMeasures: MeasuresData = response[0]?.measures || {};
        setMeasuresData(newMeasures[slug]);
      } catch (error) {
        console.error('Failed to fetch measures:', error);
      } finally {
        setLoading(false);
      }
    },
    [slug]
  );

  // 1) Initial fetch: use tenant default time range
  useEffect(() => {
    if (!initialRange.start || !initialRange.end) return;
    if (startParam || endParam) return; // If URL already has a time range, skip initial fetch
    // If URL has days=all, fetch data for default observation window preset
    if (dateRangeParam === 'all') {
      setActiveFetchRange({ start: null, end: null });
      return;
    }
    setActiveFetchRange({ start: initialRange.start, end: initialRange.end });
    fetchMeasures(initialRange.start, initialRange.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialRange]);

  // 2) startParam/endParam: skip the first transition from null → value,
  //    only fetch when it changes from one value to another value. coz first value it gets from url, at the same time when we fetch using initialRange, so we can skip the first one as it needs to be on tenant time range, subsequent ones will user selected time range.
  useEffect(() => {
    const prevStart = prevStartParam.current;
    const prevEnd = prevEndParam.current;
    const prevDateRange = prevDateRangeParam.current;
    // Always track the current values
    prevStartParam.current = startParam;
    prevEndParam.current = endParam;
    prevDateRangeParam.current = dateRangeParam;

    // If previous was null, this is the first time getting a value; and we are not changing from default observation window — don't fetch
    if ((prevStart === null || prevEnd === null) && prevDateRange !== 'all')
      return;

    // If values haven't changed, skip
    if (prevStart === startParam && prevEnd === endParam) return;

    // Handle "all" time range
    if (dateRangeParam === 'all') {
      setActiveFetchRange({ start: null, end: null });
      fetchMeasures(null, null);
      return;
    }

    if (!startParam || !endParam) return;
    setActiveFetchRange({ start: startParam, end: endParam });
    fetchMeasures(startParam, endParam);
  }, [startParam, endParam, dateRangeParam, fetchMeasures]);

  useEffect(() => {
    setMeasuresData(measures);
  }, [measures]);

  const hasIssues =
    measuresData?.errors > 0 ||
    measuresData?.not_meet_sla > 0 ||
    measuresData?.not_meet_budget_sla > 0 ||
    measuresData?.test_failures > 0;

  if (loading) {
    return (
      <div className="flex gap-1">
        <SeparatorComp />
        <Skeleton className="h-6 w-20 rounded-full bg-[#8C9EA1]" />
        <Skeleton className="h-6 w-20 rounded-full bg-[#8C9EA1]" />
      </div>
    );
  }

  if (!hasIssues) return null;

  // use selected time range or default time range
  const startDate = toFromDateRange.from || initialRange.start;
  const endDate = toFromDateRange.to || initialRange.end;

  const performanceAlert = getAlertFactConfig(data.sla_config, 'performance');
  const budgetAlert = getAlertFactConfig(data.sla_config, 'budget');
  // when no custom fact has default alert set, fall back to 'traces'
  const performanceFact =
    performanceAlert?.factType && performanceAlert.factType !== 'default'
      ? performanceAlert.factType
      : 'traces';
  const budgetFact =
    budgetAlert?.factType && budgetAlert.factType !== 'default'
      ? budgetAlert.factType
      : 'traces';

  return (
    <div className="flex items-center gap-1">
      <div className="flex gap-4">
        <SeparatorComp />
        <div className="flex items-center gap-1 whitespace-nowrap">
          {measuresData?.errors > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/*if error < 25 then viewBy=all else viewBy=reliability*/}
                  <Link
                    href={`/en/apps/${data.name}/traces?viewBy=${measuresData.errors < 25 ? 'all' : 'reliability'}&factName=traces&breakdownFilter=traces.genai.error&${activeFetchRange.start && activeFetchRange.end ? `start=${activeFetchRange.start}&end=${activeFetchRange.end}` : `days=all`}&from_measures_badge=1`}
                    className="flex h-6 items-center justify-center gap-1 whitespace-nowrap rounded-3xl bg-[#F26C743D] px-2 py-1"
                  >
                    <span>{System.InfoIcon('h-4 w-4 text-red-500')}</span>
                    <span className="text-form-input-text text-white">
                      {messages?.General?.error} {measuresData.errors}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-52 rounded"
                >
                  {messages?.Measures.app_details_error}{' '}
                  {daysCountString
                    ? `${messages?.General?.last} ${daysCountString}`
                    : localStorage.getItem('tenantDefaultTimeRange')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {measuresData?.not_meet_sla > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* for traces - land in performance view, for non-trace facts - land in default view, both with not_met_performance_sla breakdown */}
                  <Link
                    href={`/en/apps/${data.name}/traces?${FACT_VIEW_SUPPORT_MAP[performanceFact]?.includes('performance') ? `viewBy=performance&factName=${performanceFact}&breakdownFilter=${performanceFact === 'traces' ? 'traces.genai.completed.not_met_performance_sla' : `${performanceFact}.completed.not_met_performance_sla`}` : `viewBy=all&factName=${performanceFact}`}&${activeFetchRange.start && activeFetchRange.end ? `start=${activeFetchRange.start}&end=${activeFetchRange.end}` : `days=all`}&from_measures_badge=1`}
                    className="flex h-6 items-center justify-center gap-1 rounded-3xl bg-[#FFCB7829] px-2 py-1"
                  >
                    <span>{System.BarIcon('h-4 w-4 text-[#FC8A13]')}</span>
                    <span className="text-form-input-text text-white">
                      {messages?.General?.performance}{' '}
                      {measuresData.not_meet_sla}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-52 rounded"
                >
                  {`Count of ${performanceAlert?.label} with a GenAI operation that did not meet the configured SLA of ${performanceAlert?.value} ${performanceAlert?.unit} in the ${daysCountString ? `${messages?.General?.last} ${daysCountString}` : localStorage.getItem('tenantDefaultTimeRange')}`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {measuresData?.not_meet_budget_sla > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* for both traces and non-trace fact - land in default view, both with not_met_budget_sla breakdown */}
                  <Link
                    href={`/en/apps/${data.name}/traces?factName=${budgetFact}&breakdownFilter=${budgetFact === 'traces' ? 'traces.genai.completed.not_met_budget_sla' : `${budgetFact}.completed.not_met_budget_sla`}&${activeFetchRange.start && activeFetchRange.end ? `start=${activeFetchRange.start}&end=${activeFetchRange.end}` : `days=all`}&from_measures_badge=1`}
                    className="flex h-6 items-center justify-center gap-1 rounded-3xl bg-[#FFCB7829] px-2 py-1"
                  >
                    <span>{System.BudgetIcon('h-4 w-4 text-[#FC8A13]')}</span>
                    <span className="text-form-input-text text-white">
                      {messages?.General?.budget}{' '}
                      {measuresData.not_meet_budget_sla}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-52 rounded"
                >
                  {`Count of ${budgetAlert?.label} with a GenAI operation that exceeded the configured budget of ${budgetAlert?.value} ${budgetAlert?.unit} in the ${daysCountString ? `${messages?.General?.last} ${daysCountString}` : localStorage.getItem('tenantDefaultTimeRange')}`}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {measuresData?.test_failures > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href={`/en/apps/${data.name}/traces?viewBy=all&factName=tests&breakdownFilter=tests.failed&${activeFetchRange.start && activeFetchRange.end ? `start=${activeFetchRange.start}&end=${activeFetchRange.end}` : `days=all`}&from_measures_badge=1`}
                    className="flex h-6 items-center justify-center gap-1 rounded-3xl bg-[#FFCB7842] px-2 py-1"
                  >
                    <span>
                      {System.TestFailuresIcon('h-4 w-4 text-[#F4EA50]')}
                    </span>
                    <span className="text-form-input-text text-white">
                      {messages?.General?.test_failures}{' '}
                      {measuresData.test_failures}
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-52 rounded"
                >
                  {messages?.Measures.app_details_test_failures}{' '}
                  {daysCountString
                    ? `${messages?.General?.last} ${daysCountString}`
                    : localStorage.getItem('tenantDefaultTimeRange')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {startDate && endDate && (
        <p className="ml-2 text-body-sm text-form-input-disabled">
          {messages?.ApplicationDetailPage?.last_refreshed_on}{' '}
          {formatDateForMeasure(
            isUserSelectedTimeRange
              ? new Date().toISOString() // show current timestamp
              : localStorage.getItem('measuresEndTime') || endDate
          )}
        </p>
      )}
    </div>
  );
};

export default TracesMeasurePill;
